// Top-level shell: hash router switch + health polling + the top bar.

import { useEffect } from 'react';
import { useHashRoute, navigate } from './router.ts';
import { useStore } from './store/useStore.ts';
import { SetupScreen } from './screens/SetupScreen.tsx';
import { GameScreen } from './screens/GameScreen.tsx';
import { HistoryScreen } from './screens/HistoryScreen.tsx';
import { PassphraseScreen } from './screens/PassphraseScreen.tsx';

export function App() {
  const route = useHashRoute();
  const health = useStore((s) => s.health);
  const authRequired = useStore((s) => s.authRequired);

  useEffect(() => {
    const s = useStore.getState();
    void s.fetchHealth();
    const timer = setInterval(() => {
      if (!document.hidden) void useStore.getState().fetchHealth();
    }, 10000);
    return () => clearInterval(timer);
  }, []);

  // Gate before anything else: no topbar, no health badges (they'd all read
  // "offline" — every API call is 401 until the passphrase is accepted).
  if (authRequired) return <PassphraseScreen />;

  const [head, second] = route.path;
  const newGameActive = head === undefined;
  const historyActive = head === 'history';
  let screen;
  if (head === undefined) {
    screen = <SetupScreen />;
  } else if (head === 'history') {
    screen = <HistoryScreen />;
  } else if ((head === 'game' || head === 'review') && second !== undefined && /^\d+$/.test(second)) {
    screen = (
      <GameScreen
        key={`${head}-${second}`}
        id={Number(second)}
        review={head === 'review'}
        query={route.query}
      />
    );
  } else {
    screen = (
      <main className="setup-screen">
        <div className="setup-card">
          <h1 className="setup-title">Nothing here.</h1>
          <p className="setup-sub">
            That path doesn&rsquo;t lead anywhere. <a href="#/">Back to the board →</a>
          </p>
        </div>
      </main>
    );
  }

  return (
    <div className="app-shell">
      <header className="topbar">
        <button type="button" className="brand" onClick={() => navigate('/')}>
          <span className="brand-stone" aria-hidden="true" />
          learn-baduk
        </button>
        <nav className="topbar-nav" aria-label="Main">
          <a href="#/" aria-current={newGameActive ? 'page' : undefined}>New game</a>
          <a href="#/history" aria-current={historyActive ? 'page' : undefined}>History</a>
        </nav>
        <div className="topbar-health">
          {health?.katago === 'starting' && (
            <span className="badge badge-warming" title="KataGo is warming up; analysis is briefly unavailable">
              KataGo starting
            </span>
          )}
          {health?.katago === 'down' && (
            <span className="badge badge-down" title="KataGo is not running">
              KataGo down
            </span>
          )}
          {health !== null && !health.claude && (
            <span className="badge badge-down" title="ANTHROPIC_API_KEY is not set; coach chat disabled">
              coach offline
            </span>
          )}
          {health !== null && !health.gnugo && (
            <span className="badge badge-down" title="GNU Go binary not available">
              bot offline
            </span>
          )}
        </div>
      </header>
      {screen}
    </div>
  );
}
